<template>
  <div>
    <v-dialog
      v-model="isCsvDialogOpen"
      width="25%"
    >
      <template v-slot:activator="{ on }">
        <v-btn v-on="on">
          <i class="fas fa-file-csv mr-2" /> Vergleich als Excel speichern
        </v-btn>
      </template>
      <v-card class="p-4">
        <h5>Export als Excel</h5>
        <v-select
          v-model="csvContractOrientation"
          :items="csvOrientationOptions"
          box
          label="Orientierung der Verträge in Excel"
        />
        <v-checkbox
          v-model="shouldCompareGavDetails"
          label="GAV-Details vergleichen"
        />
        <v-btn
          :disabled="isDownloading"
          :loading="isDownloading"
          class="d-block"
          @click="downloadCsv()"
        >
          <i class="fas fa-file-csv mr-2" /> Speichern
        </v-btn>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="isPdfDialogOpen"
      width="25%"
    >
      <template v-slot:activator="{ on }">
        <v-btn
          :disabled="pdfExportDisabled"
          class="d-block"
          v-on="on"
        >
          <i class="fas fa-file-pdf mr-2" /> Vergleich als PDF speichern
        </v-btn>
        <span
          v-if="loadedContracts.length > 3"
          class="ml-2 error-text"
        >
          Es können nicht mehr als 3 Verträge als PDF exportiert werden.
        </span>
      </template>
      <v-card class="p-4">
        <h4>Export als PDF</h4>
        <v-select
          v-model="pdfPageOrientation"
          :disabled="pdfOrientationDisabled"
          :items="pdfPageOrientationOptions"
          box
          label="Ausrichtung"
          @change="updatePdfSettings(loadedContracts.length)"
        />
        <v-select
          v-model="pdfPageFormat"
          :disabled="pdfFormatDisabled"
          :items="pdfPageFormatOptions"
          box
          label="Format"
        />
        <span
          v-if="shouldDisplayWarning"
          class="text-danger"
        >
          Die gewählten Einstellungen können zu einem unleserlichen PDF führen.
          Es wird empfohlen, weniger Verträge auszuwählen oder den Export als
          CSV zu verwenden.
        </span>
        <v-btn
          :disabled="isDownloading"
          :loading="isDownloading"
          class="d-block"
          @click="downloadPdf()"
        >
          <i class="fas fa-file-pdf mr-2" /> Speichern
        </v-btn>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import pdfMake from "pdfmake";
import { GetExcelKlauselComparison } from "@/services/comparison.service.js";

export default {
  props: {
    pdfContent: {
      type: Array,
      required: true,
    },
    pdfFileName: {
      type: String,
      default: "export.pdf",
    },
    selectedLanguage: {
      type: String,
      default: "de-CH",
    },
    loadedContracts: {
      type: Array,
      default: () => [],
    },
    selectedCriterionTypes: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      csvOrientationOptions: ["Horizontal", "Vertikal"],
      csvContractOrientation: "Horizontal",
      pdfPageFormatOptions: ["A2", "A3", "A4"],
      pdfPageFormat: "A2",
      pdfPageOrientationOptions: ["Hochformat", "Querformat"],
      pdfPageOrientation: "Querformat",
      isDownloading: false,
      isCsvDialogOpen: false,
      isPdfDialogOpen: false,
      shouldCompareGavDetails: false,
    };
  },
  computed: {
    shouldTransposeExport() {
      return this.csvContractOrientation !== "Horizontal";
    },
    pdfExportDisabled() {
      return (
        this.loadedContracts.length !== 2 && this.loadedContracts.length !== 3
      );
    },
    pdfOrientationDisabled() {
      return (
        this.loadedContracts.length === 3 || this.loadedContracts.length < 2
      );
    },
    pdfFormatDisabled() {
      return (
        this.loadedContracts.length === 3 || this.loadedContracts.length === 2
      );
    },
    shouldDisplayWarning() {
      return (
        (this.pdfOrientation === "Hochformat" &&
          this.loadedContracts.length === 2 &&
          this.pdfPageFormat !== "A2") ||
        (this.loadedContracts.length === 3 && this.pdfPageFormat !== "A2")
      );
    },
  },
  created() {
    this.updatePdfSettings(this.loadedContracts.length);
  },
  methods: {
    downloadCsv() {
      this.isDownloading = true;

      let contractsToCompare = this.loadedContracts.map((contract) => {
        return {
          contractNumber: contract.GavNumber,
          versionNumber: contract.Version,
          editionId: contract.Edition,
        };
      });

      let klauselTypesToCompare = this.selectedCriterionTypes
        .map((x) => x.typeId)
        .filter((x) => x && x > 0);

      let comparisonRequest = {
        contractsToCompare: contractsToCompare,
        klauselTypesToCompare: klauselTypesToCompare,
      };

      GetExcelKlauselComparison(
        comparisonRequest,
        this.shouldCompareGavDetails,
        this.shouldTransposeExport,
        this.selectedLanguage,
        (response) => {
          const linkSource = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${
            response.fileBase64String
          }`;
          const downloadLink = document.createElement("a");
          const fileName = response.filename;

          this.isDownloading = false;
          downloadLink.href = linkSource;
          downloadLink.download = fileName;
          downloadLink.click();

          this.isCsvDialogOpen = false;
        },
        (error) => {
          console.error(error);
          this.isDownloading = false;
          this.isCsvDialogOpen = false;
        },
      );
    },
    downloadPdf() {
      let docDefinition = {
        pageOrientation: this.pdfOrientation,
        pageSize: this.pdfPageFormat,
        content: [
          {
            text: "Vertragsvergleich",
          },
          {
            table: {
              body: this.pdfContent,
            },
          },
        ],
        styles: {
          criterionTable: {
            fontSize: 10 - (this.pdfContent[0].length - 3) * 3,
          },
          nonave: {
            background: "#29cdff",
          },
          nonpvl: {
            background: "#f8f800",
          },
          smartcom: {
            background: "#ff000e",
          },
          tempSmartCom: {
            background: "#ff000e",
          },
        },
        defaultStyle: {
          fontSize: 12 - (this.pdfContent[0].length - 4) * 3,
        },
      };
      pdfMake.createPdf(docDefinition).download(this.pdfFileName);
      this.isPdfDialogOpen = false;
    },
  },
};
</script>

<style scoped>
.error-text {
  color: #ff0000;
}
</style>
